/**
 * [NWF] Meterware (fabric by the metre) — integer model, no app / no Shopify Function.
 *
 * Product detail page:
 *   The <nwf-meterware-selector> custom element turns a length input (cm) into an
 *   integer unit quantity (units = length / step), writes that quantity into the native
 *   product form, keeps the visible "Länge" line-item property in sync, shows a live
 *   total price and forces a unique "_cut" property per add so identical lengths stay
 *   separate cart lines.
 *
 * Cart:
 *   Meterware line items (identified by the server-rendered .nwf-cart-price) show a PAngV
 *   line (€/m²) and an editable length stepper (+/-) that keeps quantity at 1 and updates
 *   the length properties via the cart API; the Cart Transform Function re-prices the line.
 *   Quantity 1 per cut means the native price/total and item_count are already correct.
 */
(function () {
    'use strict';

    function formatMoney(cents, locale, currency) {
        var amount = (Number(cents) || 0) / 100;
        try {
            return new Intl.NumberFormat(locale || 'de', {
                style: 'currency',
                currency: currency || 'EUR',
            }).format(amount);
        } catch (error) {
            return amount.toFixed(2).replace('.', ',') + ' €';
        }
    }

    function formatLengthLabel(cm, locale) {
        var meters = (Number(cm) || 0) / 100;
        return meters.toLocaleString(locale || 'de', { maximumFractionDigits: 2 }) + ' m';
    }

    /* Preset chip label: metres with exactly one decimal ("0,3 m", "1,0 m", "2,0 m")
       so all chips read uniformly. */
    function formatPresetLabel(cm, locale) {
        var meters = (Number(cm) || 0) / 100;
        return meters.toLocaleString(locale || 'de', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' m';
    }

    /* PAngV reference area: one running metre of fabric = 1 m × width (constant per
       product), NOT the ordered cut's area. */
    function formatArea(widthCm, locale) {
        var area = (Number(widthCm) || 0) / 100;
        return area.toLocaleString(locale || 'de', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' m²';
    }

    function formatPangv(widthCm, m2Cents, locale, currency) {
        return formatArea(widthCm, locale) + ' | ' + formatMoney(m2Cents, locale, currency) + '/m²';
    }

    function generateCutId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
        return 'cut-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    /* ---------------------------------------------------------------- PDP selector */

    if (!window.customElements.get('nwf-meterware-selector')) {
        window.customElements.define(
            'nwf-meterware-selector',
            class NwfMeterwareSelector extends HTMLElement {
                connectedCallback() {
                    this.step = parseInt(this.dataset.step, 10) || 0;
                    if (this.step <= 0) {
                        return;
                    }

                    this.locale = this.dataset.locale || 'de';
                    this.currency = this.dataset.currency || 'EUR';
                    this.width = parseFloat(this.dataset.width) || 0;
                    this.sectionId = this.dataset.sectionId || '';

                    // Minimum order length (nwf.mindestlaenge, cm) expressed in step units.
                    // Absent/invalid → 1 step. Server already snaps it to a step multiple.
                    var minLength = parseInt(this.dataset.minLength, 10) || this.step;
                    this.minUnits = Math.max(1, Math.round(minLength / this.step));

                    /* NWF-108: the selector moved into the price block, which renders
                       its own <form> for Shopify's payment_terms. closest('form') would
                       silently latch onto that installment form instead of the
                       add-to-cart form, so resolve by id first and only fall back to
                       the ancestor lookup for placements that still nest it in the
                       product form. */
                    this.form =
                        (this.dataset.formId && document.getElementById(this.dataset.formId)) || this.closest('form');
                    this.lengthInput = this.querySelector('[data-nwf-length]');
                    this.lengthProperty = this.querySelector('[data-nwf-length-prop]');
                    this.lengthCmInput = this.querySelector('[data-nwf-length-cm]');
                    this.cutInput = this.querySelector('[data-nwf-cut]');
                    this.totalElement = this.querySelector('[data-nwf-total]');
                    this.pangvElement = this.querySelector('[data-nwf-pangv]');
                    this.presetTargets = (this.dataset.presets || '')
                        .split(',')
                        .map(function (value) {
                            return parseInt(value, 10);
                        })
                        .filter(function (value) {
                            return value > 0;
                        });

                    this.variants = this.parseVariants();
                    this.variant = this.resolveVariant();
                    this.quantityInput = this.form ? this.form.querySelector('input[name="quantity"]') : null;

                    /* NWF-238: a cut is always quantity 1, so Shopify's own "maximum quantity is
                       already in your cart" check never sees the length. The selector keeps its own
                       copy of the cart to add up the cuts that are already there. */
                    this.cartItems = null;
                    this.refreshCart();
                    if (window.FoxTheme && FoxTheme.pubsub && !this.cartUpdateUnsubscriber) {
                        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
                            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                            this.onCartUpdate.bind(this),
                        );
                    }

                    this.hideNativeQuantity();
                    this.hideNativePrice();
                    this.buildPresets();
                    this.bindEvents();
                    this.update(true);
                }

                parseVariants() {
                    var node = this.querySelector('.nwf-meterware__variants');
                    if (!node) {
                        return [];
                    }
                    try {
                        return JSON.parse(node.textContent) || [];
                    } catch (error) {
                        return [];
                    }
                }

                selectedVariantId() {
                    var input = this.form ? this.form.querySelector('[name="id"]') : null;
                    return input ? String(input.value) : null;
                }

                resolveVariant() {
                    var id = this.selectedVariantId();
                    var match = null;
                    if (id) {
                        match = this.variants.filter(function (variant) {
                            return String(variant.id) === id;
                        })[0];
                    }
                    return match || this.variants[0] || null;
                }

                /* Stock is tracked in step-units, so the max unit count is the inventory
           quantity (only when Shopify tracks it and does not allow overselling). */
                maxUnits() {
                    var variant = this.variant;
                    if (!variant) {
                        return null;
                    }
                    if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue') {
                        return Math.max(0, parseInt(variant.inventory_quantity, 10) || 0);
                    }
                    return null;
                }

                /* NWF-238: some cartUpdate publishers send the full cart, others only a hint;
                   anything without an items array is re-read from the cart endpoint. */
                onCartUpdate(payload) {
                    var cart = payload && payload.cart;
                    if (cart && Array.isArray(cart.items)) {
                        this.cartItems = cart.items;
                        return;
                    }
                    this.refreshCart();
                }

                refreshCart() {
                    if (!(window.FoxTheme && FoxTheme.routes && FoxTheme.utils)) {
                        return;
                    }
                    var self = this;
                    fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig('json', 'GET'))
                        .then(function (response) {
                            return response.json();
                        })
                        .then(function (cart) {
                            if (cart && Array.isArray(cart.items)) {
                                self.cartItems = cart.items;
                            }
                        })
                        .catch(function () {
                            /* No cart copy means no pre-check; cart and checkout still validate. */
                        });
                }

                /* Step units of the selected variant already in the cart, rounded like
                   nwf-meterware-over-stock and the Cart Transform Function price them. */
                unitsInCart() {
                    if (!this.variant || !this.cartItems) {
                        return 0;
                    }
                    var self = this;
                    var id = String(this.variant.id);
                    return this.cartItems.reduce(function (sum, item) {
                        var lengthCm = parseInt(item.properties && item.properties._length_cm, 10);
                        if (String(item.variant_id) !== id || !(lengthCm > 0)) {
                            return sum;
                        }
                        return sum + Math.max(self.minUnits, Math.round(lengthCm / self.step));
                    }, 0);
                }

                /* NWF-238: the error message when this cut plus the cuts in the cart need more
                   than is in stock, otherwise null. */
                cartStockError() {
                    var max = this.maxUnits();
                    if (max === null || !this.lengthInput) {
                        return null;
                    }
                    var left = max - this.unitsInCart();
                    if (this.normalizeUnits(this.lengthInput.value) <= left) {
                        return null;
                    }
                    if (left >= this.minUnits && this.dataset.msgOnlyLeft) {
                        return this.dataset.msgOnlyLeft.replace(
                            '[length]',
                            formatLengthLabel(left * this.step, this.locale),
                        );
                    }
                    return this.dataset.msgMaxInCart || null;
                }

                showFormError(message) {
                    if (this.form && typeof this.form.displayFormErrors === 'function') {
                        this.form.displayFormErrors(message);
                    } else {
                        window.alert(message);
                    }
                }

                normalizeUnits(rawLength) {
                    var minLength = this.minUnits * this.step;
                    var requested = parseInt(rawLength, 10);
                    if (isNaN(requested) || requested < minLength) {
                        requested = minLength;
                    }
                    var units = Math.round(requested / this.step);
                    if (units < this.minUnits) {
                        units = this.minUnits;
                    }
                    // NWF-216: stock below the minimum never pulls the length under it,
                    // the variant is rendered sold out instead.
                    var max = this.maxUnits();
                    if (max !== null && max >= this.minUnits && units > max) {
                        units = max;
                    }
                    return units;
                }

                /* Quick-select chips: each configured target length (cm) snapped to the
                   nearest valid multiple of the step, labelled with the actual value. */
                buildPresets() {
                    this.presetsContainer = this.querySelector('[data-nwf-presets]');
                    if (!this.presetsContainer || !this.presetTargets.length) {
                        return;
                    }
                    var self = this;
                    var max = this.maxUnits();
                    var seen = {};
                    // NWF-216: rebuilt on every variant change, the stock cap differs per colour.
                    this.presetsContainer.replaceChildren();
                    this.presetTargets.forEach(function (target) {
                        var units = Math.round(target / self.step);
                        if (units < self.minUnits) {
                            units = self.minUnits;
                        }
                        if (max !== null && max >= self.minUnits && units > max) {
                            units = max;
                        }
                        var cm = units * self.step;
                        if (seen[cm]) {
                            return;
                        }
                        seen[cm] = true;
                        var button = document.createElement('button');
                        button.type = 'button';
                        button.className = 'nwf-meterware__preset';
                        button.dataset.cm = cm;
                        button.textContent = formatPresetLabel(cm, self.locale);
                        button.addEventListener('click', function () {
                            if (self.lengthInput) {
                                self.lengthInput.value = cm;
                            }
                            self.update(true);
                        });
                        self.presetsContainer.appendChild(button);
                    });
                }

                update(commit) {
                    var rawValue = this.lengthInput ? this.lengthInput.value : this.step;
                    var units = this.normalizeUnits(rawValue);
                    var length = units * this.step;
                    this.currentUnits = units;

                    if (commit && this.lengthInput) {
                        this.lengthInput.value = length;
                    }

                    /* App model: quantity is always 1 (one cut). The length is carried in the
                       _length_cm property; the Cart Transform Function computes the price. */
                    if (this.quantityInput) {
                        this.quantityInput.value = 1;
                        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }

                    if (this.lengthProperty) {
                        this.lengthProperty.value = formatLengthLabel(length, this.locale);
                    }
                    if (this.lengthCmInput) {
                        this.lengthCmInput.value = length;
                    }

                    var price = this.variant ? this.variant.price : 0;
                    if (this.totalElement) {
                        this.totalElement.textContent = formatMoney(units * price, this.locale, this.currency);
                    }
                    if (this.pangvElement && this.width > 0 && this.step > 0) {
                        var m2Cents = Math.round((price * 10000) / (this.step * this.width));
                        this.pangvElement.textContent = formatPangv(this.width, m2Cents, this.locale, this.currency);
                    }
                    if (this.presetsContainer) {
                        this.presetsContainer.querySelectorAll('.nwf-meterware__preset').forEach(function (button) {
                            button.classList.toggle('is-active', parseInt(button.dataset.cm, 10) === length);
                        });
                    }

                    this.applySoldOut();
                }

                /* NWF-216: sold out, or stock left that is shorter than one minimum cut. */
                isSoldOut() {
                    if (!this.variant || this.variant.available === false) {
                        return true;
                    }
                    var max = this.maxUnits();
                    return max !== null && max < this.minUnits;
                }

                applySoldOut() {
                    var soldOut = this.isSoldOut();
                    /* NWF-238: "−" greys out at the minimum length, "+" at the full stock. What the cart
                       already holds is left to the error message on submit. */
                    var atMinimum = this.currentUnits !== undefined && this.currentUnits <= this.minUnits;
                    var limit = this.maxUnits();
                    var atMaximum = limit !== null && this.currentUnits >= limit;
                    this.classList.toggle('is-sold-out', soldOut);
                    this.querySelectorAll('[data-nwf-length], [data-nwf-step], .nwf-meterware__preset').forEach(
                        function (control) {
                            var step = control.dataset.nwfStep;
                            control.disabled =
                                soldOut || (atMinimum && step === 'down') || (atMaximum && step === 'up');
                        },
                    );
                }

                hideNativeQuantity() {
                    if (!this.form) {
                        return;
                    }
                    var wrapper = this.form.querySelector('.product-form__quantity');
                    if (wrapper) {
                        wrapper.classList.add('nwf-hidden');
                    }
                }

                hideNativePrice() {
                    if (!this.sectionId) {
                        return;
                    }
                    var priceElement = document.getElementById('price-' + this.sectionId);
                    if (priceElement) {
                        priceElement.classList.add('nwf-hidden');
                    }
                }

                bindEvents() {
                    var self = this;

                    if (this.lengthInput) {
                        this.lengthInput.addEventListener('input', function () {
                            self.update(false);
                        });
                        this.lengthInput.addEventListener('change', function () {
                            self.update(true);
                        });
                    }

                    this.querySelectorAll('[data-nwf-step]').forEach(function (button) {
                        button.addEventListener('click', function () {
                            var direction = button.dataset.nwfStep === 'up' ? 1 : -1;
                            var current = parseInt(self.lengthInput.value, 10);
                            if (isNaN(current)) {
                                current = self.step;
                            }
                            self.lengthInput.value = current + direction * self.step;
                            self.update(true);
                        });
                    });

                    if (this.form) {
                        /* Refresh the cut id on every submit so repeated adds of the same length
               become separate cart lines. Capture phase runs before the theme's
               product-form serialises the form. */
                        this.form.addEventListener(
                            'submit',
                            function (event) {
                                /* NWF-238: block the add like Shopify does for normal products.
                                   Capture listeners on the target run before the theme's
                                   ProductForm submit handler, so it never fires. */
                                var stockError = self.cartStockError();
                                if (stockError) {
                                    event.preventDefault();
                                    event.stopImmediatePropagation();
                                    self.showFormError(stockError);
                                    return;
                                }
                                if (self.cutInput) {
                                    self.cutInput.value = generateCutId();
                                }
                                // App model: one cut = quantity 1 (the length rides in the
                                // _length_cm property; the Function sets the price).
                                if (self.quantityInput) {
                                    self.quantityInput.value = 1;
                                }
                            },
                            true,
                        );

                        this.form.addEventListener('change', function (event) {
                            if (event.target && event.target.name === 'id') {
                                self.variant = self.resolveVariant();
                                self.buildPresets();
                                self.update(true);
                            }
                        });
                    }

                    /* NWF-184: on a Meterware product the sticky bar carries no form of
               its own - its button scrolls here instead. The listener sits on the
               document because that button lives in another section, and this
               mirrors the handler nwf-color-picker.js provides for the colour
               list, which no longer loads on these products. */
                    if (this.onScrollToSelectorClick) {
                        /* bindEvents runs again if the element is reconnected rather than
                   replaced. Dropping the previous listener first keeps it from
                   piling up - reassigning below would otherwise orphan it. */
                        document.removeEventListener('click', this.onScrollToSelectorClick);
                    }
                    this.onScrollToSelectorClick = function (event) {
                        var button = event.target.closest('[data-nwf-scroll-to-selector]');
                        if (!button) {
                            return;
                        }
                        event.preventDefault();
                        /* The sticky bar's own handler puts the button into a loading
                   state on click and only resets it on a cart event, which never
                   arrives here. */
                        button.classList.remove('btn--loading');
                        button.removeAttribute('aria-disabled');
                        self.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Focus after the scroll so the browser does not jump twice.
                        window.setTimeout(function () {
                            if (self.lengthInput) {
                                self.lengthInput.focus({ preventScroll: true });
                            }
                        }, 500);
                    };
                    document.addEventListener('click', this.onScrollToSelectorClick);
                }

                disconnectedCallback() {
                    /* A product swap replaces the whole product-info subtree, so without
               this the old element would keep answering the sticky bar and scroll
               a node that is no longer in the document. */
                    if (this.onScrollToSelectorClick) {
                        document.removeEventListener('click', this.onScrollToSelectorClick);
                        this.onScrollToSelectorClick = null;
                    }
                    if (this.cartUpdateUnsubscriber) {
                        this.cartUpdateUnsubscriber();
                        this.cartUpdateUnsubscriber = null;
                    }
                }
            },
        );
    }

    /* ------------------------------------------------------------------ cart tweaks */

    /* The plain "Länge" property row is replaced by the editable stepper, so hide it. */
    function hideLengthPropertyRow(item) {
        item.querySelectorAll('.cart-item__options dt').forEach(function (term) {
            var label = (term.textContent || '').trim().replace(/:$/, '').trim();
            if (label === 'Länge') {
                var row = term.closest('div');
                if (row) {
                    row.classList.add('nwf-hidden');
                }
            }
        });
    }

    /* Update a Meterware line's length properties (quantity stays 1) via the cart API,
       then let the theme re-render via pubsub; the Function re-prices from _length_cm. */
    function changeCartLine(line, quantity, properties) {
        if (!(window.FoxTheme && FoxTheme.routes && FoxTheme.utils && FoxTheme.pubsub)) {
            return;
        }
        var sections = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } }),
        );
        var body = JSON.stringify({ line: line, quantity: quantity, properties: properties, sections: sections });
        fetch(FoxTheme.routes.cart_change_url, Object.assign({}, FoxTheme.utils.fetchConfig(), { body: body }))
            .then(function (response) {
                return response.json();
            })
            .then(function (cart) {
                /* An error response (422 for a sold-out line, 429 while rate limited, …) is JSON
                   too, but it is not a cart. Publishing it hands the theme's cart subscribers an
                   object with no `items` — and cart.js reads `cart.errors` off whatever arrives,
                   alerts it and navigates to the cart page (NWF-168). */
                if (!cart || !Array.isArray(cart.items)) {
                    if (window.console) {
                        console.warn('[nwf-meterware] cart line not changed', cart);
                    }
                    return;
                }
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart, line: line });
            })
            .catch(function (error) {
                if (window.console) {
                    console.log(error);
                }
            });
    }

    function buildCartStepper(config) {
        var wrap = document.createElement('div');
        wrap.className = 'nwf-cart-stepper';
        wrap.innerHTML =
            '<span class="nwf-cart-stepper__label">' +
            config.label +
            '</span>' +
            '<span class="nwf-cart-stepper__control">' +
            '<button type="button" class="nwf-cart-stepper__btn" data-nwf-cart-step="down" aria-label="' +
            config.decrease +
            '"><span aria-hidden="true">&minus;</span></button>' +
            '<span class="nwf-cart-stepper__value" data-nwf-cart-value></span>' +
            '<button type="button" class="nwf-cart-stepper__btn" data-nwf-cart-step="up" aria-label="' +
            config.increase +
            '"><span aria-hidden="true">+</span></button>' +
            '</span>';

        wrap.querySelector('[data-nwf-cart-value]').textContent = formatLengthLabel(config.lengthCm, config.locale);

        if (config.soldOut) {
            wrap.classList.add('nwf-cart-stepper--disabled');
            wrap.querySelectorAll('[data-nwf-cart-step]').forEach(function (button) {
                button.disabled = true;
            });
            return wrap;
        }

        /* NWF-238: the other cuts of this colour already take their share of the stock, so "+"
           stops where this line would push the cuts together past it. */
        var lineFull = function () {
            return config.lineMax !== null && Math.round(config.lengthCm / config.step) >= config.lineMax;
        };
        var upButton = wrap.querySelector('[data-nwf-cart-step="up"]');
        if (upButton && lineFull()) {
            upButton.disabled = true;
        }
        // NWF-238: at the minimum length there is nothing left to take off, so "−" greys out.
        var downButton = wrap.querySelector('[data-nwf-cart-step="down"]');
        if (downButton && Math.round(config.lengthCm / config.step) <= config.minUnits) {
            downButton.disabled = true;
        }

        wrap.querySelectorAll('[data-nwf-cart-step]').forEach(function (button) {
            button.addEventListener('click', function () {
                var direction = button.dataset.nwfCartStep === 'up' ? 1 : -1;
                if (direction === 1 && lineFull()) {
                    return;
                }
                var currentUnits = Math.round(config.lengthCm / config.step);
                var newUnits = currentUnits + direction;
                if (newUnits < config.minUnits) {
                    newUnits = config.minUnits;
                }
                if (config.max !== null && config.max >= config.minUnits && newUnits > config.max) {
                    newUnits = config.max;
                }
                var newLength = newUnits * config.step;
                if (newLength === config.lengthCm) {
                    return;
                }
                wrap.querySelectorAll('button').forEach(function (b) {
                    b.disabled = true;
                });
                // Quantity stays 1; the length rides in the properties and the Function re-prices.
                var properties = {
                    Länge: formatLengthLabel(newLength, config.locale),
                    _length_cm: String(newLength),
                };
                if (config.cut) {
                    properties._cut = config.cut;
                }
                changeCartLine(config.line, 1, properties);
            });
        });

        return wrap;
    }

    function processCart(root) {
        var scope = root && root.querySelectorAll ? root : document;
        scope.querySelectorAll('.cart-item').forEach(function (item) {
            if (item.dataset.nwfProcessed === '1') {
                return;
            }
            // A Meterware line item is identified by the server-rendered nwf-cart-price.
            var priceEl = item.querySelector('.nwf-cart-price');
            if (!priceEl) {
                return;
            }
            item.dataset.nwfProcessed = '1';
            item.classList.add('nwf-cart-item');

            // The ordered length is shown by the stepper, so hide the plain "Länge" row.
            // The native price/total are correct in the app model (qty 1 + Function price),
            // so they are NOT hidden here.
            hideLengthPropertyRow(item);

            var step = parseInt(priceEl.dataset.nwfStep, 10) || 0;
            if (step <= 0) {
                return;
            }
            var minLengthCm = parseInt(priceEl.dataset.nwfMin, 10) || step;
            var config = {
                step: step,
                minUnits: Math.max(1, Math.round(minLengthCm / step)),
                lengthCm: parseInt(priceEl.dataset.nwfLengthCm, 10) || step,
                max: priceEl.dataset.nwfMax ? parseInt(priceEl.dataset.nwfMax, 10) : null,
                lineMax: priceEl.dataset.nwfLineMax ? parseInt(priceEl.dataset.nwfLineMax, 10) : null,
                soldOut: false,
                line: priceEl.dataset.nwfLine,
                cut: priceEl.dataset.nwfCut || '',
                locale: priceEl.dataset.nwfLocale || 'de',
                label: priceEl.dataset.nwfLabel || '',
                decrease: priceEl.dataset.nwfDecrease || '',
                increase: priceEl.dataset.nwfIncrease || '',
            };

            // NWF-221: less stock than one minimum cut is sold out (NWF-216), there is no length left to pick.
            config.soldOut = config.max !== null && config.max < Math.max(1, Math.ceil(minLengthCm / step));

            /* A line can arrive with a length below the minimum, not on a whole step multiple,
               or with a `Länge` display property that does not match `_length_cm` at all —
               none of which ever pass through the stepper's own clamp below. All three are
               possible from the order-reorder customer-account extension replaying properties
               from a past order, or any other path that writes the cart line directly (a
               hand-crafted /cart/add.js call, a manipulated ?nwf_reorder= link, …). `Länge` in
               particular matters beyond the cart page: it is the human-readable length shown on
               the order, invoice, and packing slip, so a mismatch there is a real
               wrong-cut-in-fulfillment risk even when `_length_cm` alone is fine.

               The canonical length is recomputed the same way meterware-cart-transform prices
               it (round to the nearest step, then floor up to the minimum) and BOTH properties
               are rewritten together whenever either one does not already match it — so a
               mismatched `Länge` gets corrected even though `_length_cm` alone needed no fix.
               Comparing against the current values first avoids firing a network request on
               every cart view when the line is already correct.

               This is a UX convenience, NOT the actual enforcement: it runs only if this script
               loads and this cart page renders. The real, unbypassable guard is the
               meterware-length-validation Shopify Function (naehwelt-core-app), which blocks
               checkout server-side regardless of whether this ever ran. */
            var canonicalUnits = Math.max(config.minUnits, Math.round(config.lengthCm / config.step));
            var canonicalLength = canonicalUnits * config.step;
            var canonicalLabel = formatLengthLabel(canonicalLength, config.locale);
            var currentLabel = priceEl.dataset.nwfLengthLabel || '';
            if (canonicalLength !== config.lengthCm || canonicalLabel !== currentLabel) {
                var correctedProperties = {
                    Länge: canonicalLabel,
                    _length_cm: String(canonicalLength),
                };
                if (config.cut) {
                    correctedProperties._cut = config.cut;
                }
                changeCartLine(config.line, 1, correctedProperties);
                // Render the stepper with the corrected value right away, instead of the
                // stale one, while the request above is still in flight.
                config.lengthCm = canonicalLength;
            }

            // Replace the raw unit-quantity control(s) with an editable length stepper.
            item.querySelectorAll('quantity-input').forEach(function (control) {
                control.replaceWith(buildCartStepper(config));
            });

            // PAngV reference line: "<1 m × width> m² | <price>/m²" (constant per product).
            var pangvElement = priceEl.querySelector('[data-nwf-pangv]');
            var width = parseFloat(priceEl.dataset.nwfWidth) || 0;
            var m2Cents = parseInt(priceEl.dataset.nwfM2, 10) || 0;
            if (pangvElement && width > 0) {
                pangvElement.textContent = formatPangv(
                    width,
                    m2Cents,
                    config.locale,
                    priceEl.dataset.nwfCurrency || 'EUR',
                );
            }
        });
    }

    /* Product cards / search / related: render the PAngV base price identically to the
       PDP ("<area> m² | <price>/m²"), overriding the server fallback so the number
       formatting matches. Runs everywhere (this script is loaded site-wide). */
    function processUnitPrices(root) {
        var scope = root && root.querySelectorAll ? root : document;
        scope.querySelectorAll('[data-nwf-unit-price]').forEach(function (element) {
            if (element.dataset.nwfDone === '1') {
                return;
            }
            var width = parseFloat(element.dataset.nwfWidth) || 0;
            if (width <= 0) {
                return;
            }
            element.dataset.nwfDone = '1';
            var m2Cents = parseInt(element.dataset.nwfM2, 10) || 0;
            element.textContent = formatPangv(
                width,
                m2Cents,
                element.dataset.nwfLocale || 'de',
                element.dataset.nwfCurrency || 'EUR',
            );
        });
    }

    function initCart() {
        if (window.__nwfMeterwareCartInit) {
            return;
        }
        window.__nwfMeterwareCartInit = true;

        processCart(document);
        processUnitPrices(document);

        var scheduled = false;
        var observer = new MutationObserver(function () {
            if (scheduled) {
                return;
            }
            scheduled = true;
            window.requestAnimationFrame(function () {
                scheduled = false;
                processCart(document);
                processUnitPrices(document);
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    /* Note: no cart-count correction needed in the app model — each Meterware line has
       quantity 1, so Shopify's native item_count is already correct (one cut = one item). */

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCart);
    } else {
        initCart();
    }
})();
